// Versão da plataforma lida das APIs NATIVAS da TV (não do user-agent).
//
// **Por que este módulo existe (o bug que ele conserta):**
// A LG **não põe a versão do webOS na user-agent**. O UA real da TV do dono
// (LG 55SM8600PSA, 2019), capturado via `ares-inspect` no device:
//
//   Mozilla/5.0 (Web0S; Linux/SmartTV) AppleWebKit/537.36 (KHTML, like Gecko)
//   Chrome/53.0.2785.34 Safari/537.36 WebAppManager
//
// Não há token `Web0S/x.x`. O regex de UA em `platform.ts` resolvia `version`
// pra `'0'`, e o `platformTierCeiling` tratava "sem versão" como pior caso →
// **`vlow` pra QUALQUER TV LG**, inclusive as modernas. O ramo `webOS 4 → low`
// era INALCANÇÁVEL em hardware real (só o teste o alcançava, injetando uma
// versão que a produção nunca emite).
//
// **A API certa (medida no device, não suposta):**
// `window.webOS` NÃO existe no WebView — ele vem da lib `webOSTV.js`, que não
// está no nosso bundle. O que o WebAppManager injeta de verdade é
// **`window.PalmSystem`**, e é de lá que sai a informação:
//
//   PalmSystem.deviceInfo (STRING JSON, SÍNCRONA):
//     { "modelName": "55SM8600PSA", "panelType": "LCD",
//       "platformVersion": "05.50.00", "platformVersionMajor": 5,
//       "platformVersionMinor": 50, "screenWidth": 3840, "screenHeight": 2160 }
//
// ATENÇÃO à pegadinha: esse `platformVersion` (05.50.00) é o **FIRMWARE**, NÃO
// a versão do webOS. A versão do webOS é o `sdkVersion`, e só sai de uma chamada
// **Luna** (assíncrona), confirmada no mesmo device:
//
//   luna://com.webos.service.tv.systemproperty/getSystemInfo
//     → { "sdkVersion": "4.10.0", "firmwareVersion": "05.50.00",
//         "modelName": "55SM8600PSA", "UHD": "true", "boardType": "M16P3_ATSC_KR" }
//
// Ou seja: firmware 05.50.00 ≠ webOS 5.5. A TV é **webOS 4.10** (LG 2019).
// Quem confundir os dois classifica a TV como duas gerações à frente.
//
// **Boot (por que não há flash):** a leitura síncrona (`readNativeSync`) já
// devolve modelo+firmware no 1º paint, e o `chromiumVersion` do UA sozinho já
// basta pro tier correto (ver `platform-tier.ts`). A Luna (`loadNativePlatformInfo`)
// roda DENTRO de `initSignals()`, que o `bootstrap.ts` aguarda ANTES do React
// montar — então o `sdkVersion` canônico chega a tempo do 1º render. Custo
// medido na TV: 13ms na 1ª chamada, 1-2ms nas seguintes (timeout de 500ms é
// folgado e nunca segura o boot).

/** Informação de plataforma vinda da API nativa do device. */
export interface NativePlatformInfo {
  /** Versão da PLATAFORMA (webOS `sdkVersion`, ex. '4.10.0'; Tizen
   *  `platform.version`, ex. '5.5'). `null` = não obtida — o caller NÃO deve
   *  assumir pior caso, deve cair no `chromiumVersion` (ver platform-tier.ts). */
  version: string | null
  /** Modelo comercial (ex.: '55SM8600PSA'). null quando indisponível. */
  modelName: string | null
  /** Firmware do device (webOS: '05.50.00'). NÃO é a versão do webOS —
   *  puramente diagnóstico/telemetria. */
  firmwareVersion: string | null
  /** Origem do dado — diagnóstico ("qual caminho respondeu nesta TV?"). */
  source: 'palm-system' | 'webos-lib' | 'luna' | 'tizen' | 'none'
}

const VAZIO: NativePlatformInfo = {
  version: null,
  modelName: null,
  firmwareVersion: null,
  source: 'none',
}

/** Timeout da chamada assíncrona (Luna). Medido na TV real: 13ms na 1ª chamada,
 *  1-2ms nas seguintes. 500ms é folga de ordem de magnitude — se estourar, o
 *  boot segue com o sinal do Chromium (que já classifica certo). */
const ASYNC_TIMEOUT_MS = 500

let cached: NativePlatformInfo | null = null
let loadPromise: Promise<NativePlatformInfo> | null = null

// ===== webOS =====

interface PalmDeviceInfo {
  modelName?: string
  platformVersion?: string
  [k: string]: unknown
}

/** Lê `PalmSystem.deviceInfo` — SÍNCRONO. Dá modelo + firmware, mas **não** a
 *  versão do webOS (que só vem da Luna). Por isso `version` continua null aqui. */
function readWebOSSync(): NativePlatformInfo | null {
  const palm = (window as unknown as { PalmSystem?: { deviceInfo?: unknown } }).PalmSystem
  const raw = palm?.deviceInfo
  if (typeof raw !== 'string') return null
  try {
    const d = JSON.parse(raw) as PalmDeviceInfo
    return {
      version: null, // `platformVersion` do PalmSystem é FIRMWARE, não webOS.
      modelName: typeof d.modelName === 'string' ? d.modelName : null,
      firmwareVersion: typeof d.platformVersion === 'string' ? d.platformVersion : null,
      source: 'palm-system',
    }
  } catch {
    return null
  }
}

/** Versão do webOS via a lib oficial `webOSTV.js` (`window.webOS.deviceInfo`),
 *  QUANDO presente. Não está no nosso bundle hoje (medido: `window.webOS` é
 *  `undefined` na TV real), mas se um dia entrar, é a fonte preferencial e o
 *  código já a consome. */
function loadWebOSLib(): Promise<NativePlatformInfo | null> {
  const api = window.webOS?.deviceInfo
  if (typeof api !== 'function') return Promise.resolve(null)
  return new Promise((resolve) => {
    let done = false
    const timer = setTimeout(() => {
      if (!done) {
        done = true
        resolve(null)
      }
    }, ASYNC_TIMEOUT_MS)
    try {
      api((d) => {
        if (done) return
        done = true
        clearTimeout(timer)
        resolve({
          version: typeof d.sdkVersion === 'string' ? d.sdkVersion : null,
          modelName: typeof d.modelName === 'string' ? d.modelName : null,
          firmwareVersion: null,
          source: 'webos-lib',
        })
      })
    } catch {
      if (!done) {
        done = true
        clearTimeout(timer)
        resolve(null)
      }
    }
  })
}

interface ServiceBridge {
  onservicecallback?: (msg: string) => void
  call: (uri: string, params: string) => void
}

/** Versão do webOS via **Luna** (`WebOSServiceBridge`) — o caminho que REALMENTE
 *  responde no WebView (validado na TV do dono: `sdkVersion: "4.10.0"`). */
function loadWebOSLuna(): Promise<NativePlatformInfo | null> {
  const Bridge = (window as unknown as { WebOSServiceBridge?: new () => ServiceBridge })
    .WebOSServiceBridge
  if (typeof Bridge !== 'function') return Promise.resolve(null)

  return new Promise((resolve) => {
    let done = false
    const finish = (v: NativePlatformInfo | null): void => {
      if (done) return
      done = true
      clearTimeout(timer)
      resolve(v)
    }
    const timer = setTimeout(() => finish(null), ASYNC_TIMEOUT_MS)

    try {
      const bridge = new Bridge()
      bridge.onservicecallback = (msg: string): void => {
        try {
          const r = JSON.parse(msg) as {
            returnValue?: boolean
            sdkVersion?: string
            firmwareVersion?: string
            modelName?: string
          }
          if (r.returnValue === false) return finish(null)
          finish({
            version: typeof r.sdkVersion === 'string' ? r.sdkVersion : null,
            modelName: typeof r.modelName === 'string' ? r.modelName : null,
            firmwareVersion: typeof r.firmwareVersion === 'string' ? r.firmwareVersion : null,
            source: 'luna',
          })
        } catch {
          finish(null)
        }
      }
      bridge.call(
        'luna://com.webos.service.tv.systemproperty/getSystemInfo',
        JSON.stringify({ keys: ['sdkVersion', 'firmwareVersion', 'modelName'] }),
      )
    } catch {
      finish(null)
    }
  })
}

// ===== Tizen (Samsung) =====
//
// ⚠️ NÃO TESTADO EM DEVICE REAL — não há TV Samsung disponível. Implementado por
// ANALOGIA com o furo do webOS, e defensivo por construção: todo acesso é
// try/catch e qualquer falha devolve `null`, o que faz o `platform-tier` cair no
// `chromiumVersion` do UA (que já classifica Tizen corretamente hoje). Ou seja,
// o pior caso desta função é "não piora nada".
//
// Nota: ao contrário da LG, a Samsung **põe** a versão no UA (`Tizen 6.0`), então
// o furo aqui é bem menor — isto é reforço, não a única fonte.

interface TizenProductInfo {
  getFirmware?: () => string
  getModelCode?: () => string
  getRealModel?: () => string
}

/** Tizen expõe tudo SÍNCRONO (`tizen.systeminfo.getCapability` + `webapis.productinfo`).
 *  NÃO TESTADO em device real. */
function readTizenSync(): NativePlatformInfo | null {
  let version: string | null = null
  let modelName: string | null = null
  let firmwareVersion: string | null = null

  try {
    const cap = window.tizen?.systeminfo?.getCapability
    if (typeof cap === 'function') {
      const v = cap('http://tizen.org/feature/platform.version')
      if (typeof v === 'string' && v.length > 0) version = v
      try {
        const m = cap('http://tizen.org/system/model_name')
        if (typeof m === 'string' && m.length > 0) modelName = m
      } catch {
        /* capability opcional */
      }
    }
  } catch {
    /* API ausente/negada — cai no Chromium do UA */
  }

  try {
    const pi = (window as unknown as { webapis?: { productinfo?: TizenProductInfo } }).webapis
      ?.productinfo
    if (pi) {
      if (typeof pi.getFirmware === 'function') firmwareVersion = pi.getFirmware()
      if (modelName === null && typeof pi.getRealModel === 'function') modelName = pi.getRealModel()
      if (modelName === null && typeof pi.getModelCode === 'function') modelName = pi.getModelCode()
    }
  } catch {
    /* webapis é específico de TV Samsung; ausente em emulador/browser */
  }

  if (version === null && modelName === null && firmwareVersion === null) return null
  return { version, modelName, firmwareVersion, source: 'tizen' }
}

// ===== API pública =====

function isWebOS(): boolean {
  const ua = navigator.userAgent.toLowerCase()
  // VIDAA (Hisense) pode conter "web0s" no UA mas NÃO tem PalmSystem — o guard
  // real é a presença dos globais, checada dentro de cada reader.
  return ua.includes('web0s') || ua.includes('webos')
}

function isTizen(): boolean {
  return navigator.userAgent.toLowerCase().includes('tizen')
}

/** Leitura SÍNCRONA das APIs nativas — o que dá pra saber no 1º paint, sem
 *  esperar callback. No webOS devolve modelo + firmware (a versão do webOS só
 *  vem da Luna); no Tizen devolve tudo. Cacheado. */
export function getNativePlatformInfo(): NativePlatformInfo {
  if (cached) return cached
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return VAZIO

  let info: NativePlatformInfo | null = null
  if (isWebOS()) info = readWebOSSync()
  else if (isTizen()) info = readTizenSync()

  cached = info ?? VAZIO
  return cached
}

/** Resolve a versão CANÔNICA da plataforma via API assíncrona (Luna, no webOS) e
 *  atualiza o cache. Idempotente — chamadas concorrentes compartilham a promise.
 *
 *  Chamado por `initSignals()`, que o `bootstrap.ts` aguarda ANTES de montar o
 *  React → o tier já sai correto no 1º render (sem flash de tier). Nunca rejeita:
 *  falha/timeout devolve o que já se sabia (o Chromium do UA cobre). */
export async function loadNativePlatformInfo(): Promise<NativePlatformInfo> {
  if (loadPromise) return loadPromise

  loadPromise = (async (): Promise<NativePlatformInfo> => {
    const base = getNativePlatformInfo()
    if (typeof window === 'undefined' || !isWebOS()) return base

    // Ordem: lib oficial `webOSTV.js` (se algum dia entrar no bundle) → Luna
    // (o que responde de fato hoje). Ambas só ADICIONAM a versão; modelo e
    // firmware do PalmSystem síncrono são preservados quando a async não os traz.
    const async_ = (await loadWebOSLib()) ?? (await loadWebOSLuna())
    if (!async_) return base

    cached = {
      version: async_.version ?? base.version,
      modelName: async_.modelName ?? base.modelName,
      firmwareVersion: async_.firmwareVersion ?? base.firmwareVersion,
      source: async_.source,
    }
    return cached
  })()

  return loadPromise
}

// ===== Test helpers =====

/** Injeta info nativa (testes). */
export function __setNativePlatformForTests(info: NativePlatformInfo | null): void {
  cached = info
  loadPromise = null
}

/** Zera o cache (testes). */
export function __resetNativePlatformForTests(): void {
  cached = null
  loadPromise = null
}
